import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminSearchInput from '../common/AdminSearchInput';
import {
  Container,
  Content,
  Header,
  Title,
  FilterSection,
  FilterControls,
  Dropdown,
  DropdownSelect,
  DropdownButton,
  DropdownIcon,
  TableContainer,
  TableInner,
  TableHeader,
  HeaderCell,
  TableBody,
  TableRow,
  Cell,
  PostTitle,
  StatusBadge,
  ActionButtons,
  DeleteButton,
  NoData,
  Loading,
  ErrorMessage,
  RetryButton,
  ModalOverlay,
  DeleteModal,
  DeleteIcon,
  DeleteModalTitle,
  DeleteWarning,
  DeleteActions,
  CancelButton,
  ConfirmButton,
  CloseButton,
  ModalActions,
  AnswerModal,
  AnswerModalHeader,
  AnswerModalTitle,
  AnswerModalContent,
  QuestionInfo,
  InfoRow,
  InfoItem,
  InfoLabel,
  InfoValue,
  QuestionContent,
  AnswerSection,
  AnswerLabel,
  AnswerEditor,
  AnswerToolbar,
  ToolbarLeft,
  SubmitButton,
  AnswerButton,
  EditButton,
  MentorSelection,
  MentorLabel,
  MentorSelect,
  AttachmentList,
  AttachmentLabel,
  AttachmentItem,
  AttachmentName,
  AttachmentRemove,
} from './LectureQAManagementLayoutStyles';
import { AdminDateFilterModal } from '../common';
import CommunityTablePagination from './CommunityTablePagination';
import {
  lectureQAManagementApi,
  mentorApi,
  LectureQAManagementItem,
  LectureQAManagementListParams,
  LectureQADetail,
  Mentor,
  Attachment
} from '../../../services/admin/lectureQAManagementApi';
import { lectureCategoryApi, LectureCategory } from '../../../services/admin/lectureCategoryApi';
import RichTextEditor from '../../common/RichTextEditor';

const LectureQAManagementComponent: React.FC = () => {
  const navigate = useNavigate();
  const [questions, setQuestions] = useState<LectureQAManagementItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [categories, setCategories] = useState<LectureCategory[]>([]);

  // 필터 상태
  const [selectedCategoryId, setSelectedCategoryId] = useState<number | undefined>(undefined);
  const [selectedStatus, setSelectedStatus] = useState(''); // 기본값: 전체

  // 날짜 필터 상태 (적용된 날짜)
  const [appliedStartDate, setAppliedStartDate] = useState('');
  const [appliedEndDate, setAppliedEndDate] = useState('');

  // 날짜 모달 상태 (임시 선택 날짜)
  const [showDateModal, setShowDateModal] = useState(false);
  const [tempStartDate, setTempStartDate] = useState('');
  const [tempEndDate, setTempEndDate] = useState('');

  // 검색 및 페이지네이션
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalElements, setTotalElements] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  // 모달 상태
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteQuestionId, setDeleteQuestionId] = useState<number | null>(null);
  const [showAnswerModal, setShowAnswerModal] = useState(false);
  const [selectedQuestion, setSelectedQuestion] = useState<LectureQAManagementItem | null>(null);
  const [questionDetail, setQuestionDetail] = useState<LectureQADetail | null>(null);
  const [answerContent, setAnswerContent] = useState('');
  const [selectedMentor, setSelectedMentor] = useState<number | undefined>(undefined);
  const [mentors, setMentors] = useState<Mentor[]>([]);
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [isEditMode, setIsEditMode] = useState(false);

  // 초기 로딩 여부 추적 (첫 번째 fetch 완료 전까지만 true)
  const isFirstLoad = useRef(true);

  // 날짜 형식 검증 함수
  const isValidDateFormat = useCallback((dateString: string): boolean => {
    const regex = /^\d{4}-\d{2}-\d{2}$/;
    if (!regex.test(dateString)) return false;

    const date = new Date(dateString);
    return date instanceof Date && !isNaN(date.getTime()) && dateString === date.toISOString().split('T')[0];
  }, []);

  // 날짜 범위 유효성 검증 함수
  const validateDateRange = useCallback((fromDate: string, toDate: string): boolean => {
    try {
      // 둘 다 비어있으면 유효
      if (!fromDate && !toDate) return true;

      // 하나만 있으면 유효
      if (!fromDate || !toDate) return true;

      // 둘 다 유효한 날짜 형식인지 확인
      if (!isValidDateFormat(fromDate) || !isValidDateFormat(toDate)) {
        /// console.error('유효하지 않은 날짜 형식:', { fromDate, toDate });
        return false;
      }

      const fromDateObj = new Date(fromDate);
      const toDateObj = new Date(toDate);

      // from이 to보다 뒤에 있는지 확인
      if (fromDateObj > toDateObj) {
        /// console.error('시작 날짜가 종료 날짜보다 뒤에 있습니다:', { fromDate, toDate });
        return false;
      }

      return true;
    } catch (error) {
      /// console.error('날짜 범위 검증 중 오류 발생:', error, { fromDate, toDate });
      return false;
    }
  }, [isValidDateFormat]);

  // 카테고리 목록 조회
  const fetchCategories = useCallback(async () => {
    try {
      const response = await lectureCategoryApi.getLectureCategories();
      setCategories(response.data);

      // 기본값을 전체(undefined)로 설정
      setSelectedCategoryId(undefined);
    } catch (err) {
      /// console.error('강의 카테고리 목록 조회 실패:', err);
    }
  }, []);

  // 강연자 목록 조회
  const fetchMentors = useCallback(async () => {
    try {
      const response = await mentorApi.getMentors();
      /// console.log('강연자 목록 조회 응답:', {response});

      // 응답이 배열인지 확인
      if (Array.isArray(response)) {
        setMentors(response);

        // 첫 번째 강연자를 기본값으로 설정
        if (response.length > 0) {
          setSelectedMentor(response[0].id);
        }
      } else {
        /// console.error('Mentors response is not an array:', response);
        setMentors([]);
      }
    } catch (err) {
      /// console.error('강연자 목록 조회 실패:', err);
      setMentors([]);
    }
  }, []);

  // 강의 질문 목록 조회
  const fetchQuestions = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // 날짜 범위 유효성 검증
      if (!validateDateRange(appliedStartDate, appliedEndDate)) {
        /// console.error('유효하지 않은 날짜 범위로 인해 날짜 필터를 초기화합니다:', { fromDate: appliedStartDate, toDate: appliedEndDate });
        alert('유효하지 않은 날짜 범위입니다. 전체 기간으로 초기화됩니다.');
        // 날짜 필터 초기화
        setAppliedStartDate('');
        setAppliedEndDate('');
        setTempStartDate('');
        setTempEndDate('');

        // 초기화된 상태로 다시 호출
        const params: LectureQAManagementListParams = {
          page: currentPage - 1,
          size: 10,
          lectureCategoryId: selectedCategoryId,
          lectureId: undefined,
          from: undefined,
          to: undefined,
          status: selectedStatus === 'true' ? true : selectedStatus === 'false' ? false : undefined,
          query: searchQuery.trim() || undefined
        };

        const response = await lectureQAManagementApi.getLectureQAList(params);
        setQuestions(response.content);
        setTotalPages(response.totalPages);
        setTotalElements(response.totalElements);
        return;
      }

      const params: LectureQAManagementListParams = {
        page: currentPage - 1,
        size: 10,
        lectureCategoryId: selectedCategoryId,
        lectureId: undefined, // 필요시 추가
        from: appliedStartDate || undefined,
        to: appliedEndDate || undefined,
        status: selectedStatus === 'true' ? true : selectedStatus === 'false' ? false : undefined,
        query: searchQuery.trim() || undefined
      };

      const response = await lectureQAManagementApi.getLectureQAList(params);
      setQuestions(response.content);
      setTotalPages(response.totalPages);
      setTotalElements(response.totalElements);
    } catch (err: any) {
      setError(err.message || '강의 질문 목록을 불러오는데 실패했습니다.');
      /// console.error('Error fetching lecture questions:', err);
    } finally {
      isFirstLoad.current = false;
      setLoading(false);
    }
  }, [currentPage, selectedCategoryId, appliedStartDate, appliedEndDate, selectedStatus, searchQuery, validateDateRange]);

  // 컴포넌트 마운트 시 데이터 로드
  useEffect(() => {
    fetchCategories();

    // 날짜 기본값을 전체 기간으로 설정 (빈 문자열로 설정)
    setAppliedStartDate('');
    setAppliedEndDate('');
    setTempStartDate('');
    setTempEndDate('');
  }, [fetchCategories]);

  useEffect(() => {
    fetchQuestions();
  }, [fetchQuestions]);

  // 삭제 처리
  const handleDeleteClick = (questionId: number) => {
    setDeleteQuestionId(questionId);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (deleteQuestionId) {
      try {
        await lectureQAManagementApi.deleteLectureQA(deleteQuestionId);
        setShowDeleteModal(false);
        setDeleteQuestionId(null);
        await fetchQuestions();
      } catch (err: any) {
        setError(err.message || '강의 질문 삭제에 실패했습니다.');
        /// console.error('Error deleting question:', err);
      }
    }
  };

  // 게시글 클릭 핸들러
  const handleQuestionClick = (questionId: number) => {
    navigate(`/community/post/${questionId}`);
  };

  // 답변 클릭 핸들러
  const handleAnswerClick = async (question: LectureQAManagementItem) => {
    setSelectedQuestion(question);
    setIsEditMode(false);
    setAnswerContent('');
    setAttachments([]);

    try {
      // 질문 상세 정보와 강연자 목록을 병렬로 가져오기
      const [detail] = await Promise.all([
        lectureQAManagementApi.getLectureQADetail(question.id),
        mentors.length === 0 ? fetchMentors() : Promise.resolve()
      ]);
      setQuestionDetail(detail);
      setShowAnswerModal(true);
    } catch (err: any) {
      setError(err.message || '질문 상세 정보를 불러오는데 실패했습니다.');
      /// console.error('Error fetching question detail:', err);
    }
  };

  // 답변 수정 클릭 핸들러
  const handleEditAnswerClick = async (question: LectureQAManagementItem) => {
    setSelectedQuestion(question);
    setIsEditMode(true);

    try {
      // 질문 상세 정보와 강연자 목록을 병렬로 가져오기
      const [detail] = await Promise.all([
        lectureQAManagementApi.getLectureQADetail(question.id),
        mentors.length === 0 ? fetchMentors() : Promise.resolve()
      ]);
      setQuestionDetail(detail);

      // 기존 답변 데이터 설정 (HTML을 마크다운으로 변환)
      if (detail.answer_content) {
        // HTML을 마크다운으로 변환하는 간단한 함수
        const htmlToMarkdown = (html: string) => {
          return html
            .replace(/<br\s*\/?>/gi, '\n')
            .replace(/<pre><code>([\s\S]*?)<\/code><\/pre>/gi, '```\n$1\n```')
            .replace(/<code>([^<]*)<\/code>/gi, '`$1`')
            .replace(/<[^>]*>/g, '')
            .replace(/&lt;/g, '<')
            .replace(/&gt;/g, '>')
            .replace(/&amp;/g, '&');
        };

        setAnswerContent(htmlToMarkdown(detail.answer_content));

        // 기존 답변의 강연자 ID 설정 (API에서 제공되지 않으므로 기본값 유지)
        // setSelectedMentor는 이미 첫 번째 강연자로 설정되어 있음

        // answer_images를 Attachment 형태로 변환
        const attachments = detail.answer_images.map((image, index) => ({
          id: index + 1,
          filename: image.split('/').pop() || `image_${index + 1}`,
          url: image,
          size: 0, // 이미지 크기는 알 수 없으므로 0으로 설정
          type: 'image' // 기본 타입 설정
        }));
        setAttachments(attachments);
      } else {
        setAnswerContent('');
        setAttachments([]);
      }

      setShowAnswerModal(true);
    } catch (err: any) {
      setError(err.message || '질문 상세 정보를 불러오는데 실패했습니다.');
      /// console.error('Error fetching question detail:', err);
    }
  };

  // 마크다운 코드 블록을 HTML로 변환하는 함수 (InquiryListComponent와 동일)
  const renderContent = (content: string) => {
    // ```로 감싸진 코드 블록을 <pre><code>로 변환
    const codeBlockRegex = /```[\w]*\n?([\s\S]*?)```/g;
    const htmlContent = content.replace(codeBlockRegex, (match, code) => {
      // 줄 번호를 위해 각 줄을 span.tbc-line으로 래핑
      const lines = code
        .replace(/\r?\n$/, '')
        .split(/\r?\n/)
        .map((l: string) => `<span class="tbc-line">${l.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')}</span>`)
        .join('\n');
      // 간단 토큰 하이라이트 (태그/속성/문자열/주석)
      let highlighted = lines
        .replace(/(&lt;\/?)([a-zA-Z0-9_-]+)(?=[\s&gt;])/g, '$1<span class="tbc-token-tag">$2</span>')
        .replace(/([a-zA-Z0-9_-]+)=(&quot;.*?&quot;|'.*?')/g, '<span class="tbc-token-attr">$1</span>=<span class="tbc-token-string">$2</span>')
        .replace(/(&lt;!--[\s\S]*?--&gt;)/g, '<span class="tbc-token-comment">$1</span>');
      return `<pre><code>${highlighted}</code></pre>`;
    });

    // 인라인 코드 `code`를 <code>로 변환
    const inlineCodeRegex = /`([^`]+)`/g;
    const finalContent = htmlContent.replace(inlineCodeRegex, '<code>$1</code>');

    // 줄바꿈을 <br>로 변환
    return finalContent.replace(/\n/g, '<br>');
  };

  // 답변 등록/수정 핸들러
  const handleAnswerSubmit = async () => {
    if (!selectedQuestion || !answerContent.trim()) {
      return;
    }

    try {
      // HTML 콘텐츠로 변환
      const htmlContent = renderContent(answerContent);

      if (isEditMode && questionDetail?.answer_content && questionDetail?.answer_id && selectedMentor) {
        // 답변 수정
        await lectureQAManagementApi.updateLectureQAAnswer(
          selectedQuestion.id,
          questionDetail.answer_id,
          htmlContent,
          selectedMentor
        );
      } else if (selectedMentor) {
        // 답변 등록
        await lectureQAManagementApi.submitLectureQAAnswer(selectedQuestion.id, htmlContent, selectedMentor);
      } else {
        setError('담당 강연자를 선택해주세요.');
        return;
      }

      setShowAnswerModal(false);
      setSelectedQuestion(null);
      setQuestionDetail(null);
      setAnswerContent('');
      setAttachments([]);
      setIsEditMode(false);
      await fetchQuestions(); // 목록 새로고침
    } catch (err: any) {
      setError(err.message || (isEditMode ? '답변 수정에 실패했습니다.' : '답변 등록에 실패했습니다.'));
      /// console.error('Error submitting answer:', err);
    }
  };

  // HTML 태그 제거 함수
  const stripHtmlTags = (html: string): string => {
    if (!html) return '';
    return html.replace(/<[^>]*>/g, '').trim();
  };

  // 페이지네이션 렌더링

  const isInitialLoading = loading && isFirstLoad.current;
  if (isInitialLoading) {
    return (
      <Container>
        <Header>
          <Title>강의 질문 답변 관리</Title>
        </Header>
        <Content>
          <Loading>로딩 중...</Loading>
        </Content>
      </Container>
    );
  }

  if (error && questions.length === 0) {
    return (
      <Container>
        <Header>
          <Title>강의 질문 답변 관리</Title>
        </Header>
        <Content>
          <ErrorMessage>
            {error}
            <RetryButton onClick={fetchQuestions}>
              다시 시도
            </RetryButton>
          </ErrorMessage>
        </Content>
      </Container>
    );
  }

  return (
    <Container>
      <Header>
        <Title>강의 질문 답변 관리</Title>
      </Header>

      <Content>
        {/* 필터 및 검색 섹션 */}
        <FilterSection>
          <FilterControls>
            {/* 카테고리 드롭다운 */}
            <Dropdown>
              <DropdownSelect
                value={selectedCategoryId || ''}
                onChange={(e) => setSelectedCategoryId(e.target.value ? parseInt(e.target.value) : undefined)}
              >
                <option value="">전체</option>
                {categories.map((category) => (
                  <option key={category.id} value={category.id}>
                    {category.name}
                  </option>
                ))}
              </DropdownSelect>
              <DropdownIcon width="14" height="14" viewBox="0 0 14 14">
                <path d="M4.17 5.25L7 8.08L9.83 5.25" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </DropdownIcon>
            </Dropdown>

            {/* 작성일 버튼 */}
            <Dropdown>
              <DropdownButton
                onClick={() => {
                  setShowDateModal(true);
                  // 모달 열 때 현재 적용된 날짜를 임시 날짜로 설정
                  setTempStartDate(appliedStartDate);
                  setTempEndDate(appliedEndDate);
                }}
                style={{ cursor: 'pointer', textAlign: 'left' }}
              >
                {appliedStartDate && appliedEndDate ? `작성일: ${appliedStartDate} ~ ${appliedEndDate}` :
                 appliedStartDate ? `작성일: ${appliedStartDate}` : '작성일'}
              </DropdownButton>
              <DropdownIcon width="14" height="14" viewBox="0 0 14 14">
                <path d="M4.17 5.25L7 8.08L9.83 5.25" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </DropdownIcon>
            </Dropdown>

            {/* 상태 드롭다운 */}
            <Dropdown>
              <DropdownSelect
                value={selectedStatus}
                onChange={(e) => setSelectedStatus(e.target.value)}
              >
                <option value="">상태</option>
                <option value="true">답변완료</option>
                <option value="false">답변대기</option>
              </DropdownSelect>
              <DropdownIcon width="14" height="14" viewBox="0 0 14 14">
                <path d="M4.17 5.25L7 8.08L9.83 5.25" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </DropdownIcon>
            </Dropdown>
          </FilterControls>

          {/* 검색 입력 */}
          <AdminSearchInput
            value={searchQuery}
            onChange={(value) => {
              setSearchQuery(value);
              setCurrentPage(1);
            }}
          />
        </FilterSection>

        {/* 테이블 */}
        <TableContainer>
          <TableInner>
            <TableHeader $columns="100px minmax(240px, 1fr) 100px 100px 150px 110px">
              <HeaderCell>연번</HeaderCell>
              <HeaderCell>제목</HeaderCell>
              <HeaderCell>상태</HeaderCell>
              <HeaderCell>작성자</HeaderCell>
              <HeaderCell>작성일</HeaderCell>
              <HeaderCell>관리</HeaderCell>
            </TableHeader>

            <TableBody>
              {questions.length > 0 ? (
                questions.map((question, index) => (
                  <TableRow key={question.id} $columns="100px minmax(240px, 1fr) 100px 100px 150px 110px">
                    <Cell>
                      {totalElements - (currentPage - 1) * 10 - index}
                    </Cell>
                    <Cell>
                      <PostTitle
                        title={stripHtmlTags(question.title)}
                        onClick={() => handleQuestionClick(question.id)}
                        style={{ cursor: 'pointer', color: 'var(--Primary-Normal, #448181)' }}
                      >
                        {stripHtmlTags(question.title)}
                      </PostTitle>
                    </Cell>
                    <Cell>
                      <StatusBadge $answered={question.answered}>
                        {question.answered ? '답변 완료' : '답변 전'}
                      </StatusBadge>
                    </Cell>
                    <Cell>
                      {question.writer_name}
                    </Cell>
                    <Cell>
                      {new Date(question.created_at).toLocaleDateString('ko-KR').replace(/\./g, '.').replace(/\s/g, '')}
                    </Cell>
                    <Cell>
                      <ActionButtons>
                        {!question.answered ? (
                          <AnswerButton
                            onClick={() => handleAnswerClick(question)}
                          >
                            답변
                          </AnswerButton>
                        ) : (
                          <EditButton
                            onClick={() => handleEditAnswerClick(question)}
                          >
                            수정
                          </EditButton>
                        )}
                        <DeleteButton
                          onClick={() => handleDeleteClick(question.id)}
                        >
                          삭제
                        </DeleteButton>
                      </ActionButtons>
                    </Cell>
                  </TableRow>
                ))
              ) : (
                <NoData>
                  등록된 강의 질문이 없습니다.
                </NoData>
              )}
            </TableBody>
          </TableInner>
          <CommunityTablePagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
          />
        </TableContainer>

        {/* 페이지네이션 */}
      </Content>

      {/* 삭제 확인 모달 */}
      {showDeleteModal && (
        <DeleteConfirmModal
          onConfirm={handleDeleteConfirm}
          onCancel={() => {
            setShowDeleteModal(false);
            setDeleteQuestionId(null);
          }}
        />
      )}

      {/* 날짜 선택 모달 */}
      {showDateModal && (
        <AdminDateFilterModal
          title="작성일 선택"
          startDate={tempStartDate}
          endDate={tempEndDate}
          onStartDateChange={(date) => setTempStartDate(date)}
          onEndDateChange={(date) => setTempEndDate(date)}
          onConfirm={() => {
            // 적용 버튼을 눌렀을 때만 실제 날짜 필터를 적용
            setAppliedStartDate(tempStartDate);
            setAppliedEndDate(tempEndDate);
            setShowDateModal(false);
            setCurrentPage(1);
          }}
          onCancel={() => {
            // 취소 시 임시 날짜를 원래 적용된 날짜로 되돌림
            setTempStartDate(appliedStartDate);
            setTempEndDate(appliedEndDate);
            setShowDateModal(false);
          }}
        />
      )}

      {/* 답변 등록 모달 */}
      {showAnswerModal && selectedQuestion && (
        <AnswerModalComponent
          question={selectedQuestion}
          questionDetail={questionDetail}
          answerContent={answerContent}
          onAnswerContentChange={setAnswerContent}
          selectedMentor={selectedMentor}
          onMentorChange={setSelectedMentor}
          mentors={mentors}
          attachments={attachments}
          onAttachmentAdd={(file) => {
            // TODO: 첨부파일 업로드 로직 구현
            /// console.log('첨부파일 추가:', file);
          }}
          onAttachmentRemove={(attachmentId) => {
            setAttachments(prev => prev.filter(att => att.id !== attachmentId));
          }}
          isEditMode={isEditMode}
          onSubmit={handleAnswerSubmit}
          onCancel={() => {
            setShowAnswerModal(false);
            setSelectedQuestion(null);
            setQuestionDetail(null);
            setAnswerContent('');
            setAttachments([]);
            setIsEditMode(false);
          }}
        />
      )}
    </Container>
  );
};

// 답변 등록 모달 컴포넌트
interface AnswerModalProps {
  question: LectureQAManagementItem;
  questionDetail: LectureQADetail | null;
  answerContent: string;
  onAnswerContentChange: (content: string) => void;
  selectedMentor: number | undefined;
  onMentorChange: (mentorId: number) => void;
  mentors: Mentor[];
  attachments: Attachment[];
  onAttachmentAdd: (file: File) => void;
  onAttachmentRemove: (attachmentId: number) => void;
  isEditMode: boolean;
  onSubmit: () => void;
  onCancel: () => void;
}

const AnswerModalComponent: React.FC<AnswerModalProps> = ({
  question,
  questionDetail,
  answerContent,
  onAnswerContentChange,
  selectedMentor,
  onMentorChange,
  mentors,
  attachments,
  onAttachmentAdd,
  onAttachmentRemove,
  isEditMode,
  onSubmit,
  onCancel
}) => {
  // HTML 태그 제거 함수
  const stripHtmlTags = (html: string): string => {
    if (!html) return '';
    return html.replace(/<[^>]*>/g, '').trim();
  };

  return (
    <ModalOverlay onClick={onCancel}>
      <AnswerModal onClick={(e) => e.stopPropagation()}>
        <AnswerModalHeader>
          <AnswerModalTitle>{isEditMode ? '답변 수정' : '답변 등록'}</AnswerModalTitle>
          <CloseButton onClick={onCancel}>×</CloseButton>
        </AnswerModalHeader>

        <AnswerModalContent>
          {/* 질문 정보 */}
          <QuestionInfo>
            <InfoRow>
              <InfoItem>
                <InfoLabel>연번</InfoLabel>
                <InfoValue>1</InfoValue>
              </InfoItem>
              <InfoItem>
                <InfoLabel>작성자</InfoLabel>
                <InfoValue>{question.writer_name}</InfoValue>
              </InfoItem>
              <InfoItem>
                <InfoLabel>작성일</InfoLabel>
                <InfoValue>{new Date(question.created_at).toLocaleDateString('ko-KR').replace(/\./g, '.').replace(/\s/g, '')}</InfoValue>
              </InfoItem>
            </InfoRow>

            <InfoRow>
              <InfoItem>
                <InfoLabel>제목</InfoLabel>
                <InfoValue>{stripHtmlTags(question.title)}</InfoValue>
              </InfoItem>
            </InfoRow>

            <InfoRow>
              <InfoItem>
                <InfoLabel>내용</InfoLabel>
                <QuestionContent>
                  {questionDetail ? stripHtmlTags(questionDetail.content) : stripHtmlTags(question.title)}
                </QuestionContent>
              </InfoItem>
            </InfoRow>
          </QuestionInfo>

          {/* 답변 입력 */}
          <AnswerSection>
            <AnswerLabel>답변</AnswerLabel>

            {/* 강연자 선택 */}
            <MentorSelection>
              <MentorLabel>담당 강연자</MentorLabel>
              <MentorSelect
                value={selectedMentor || ''}
                onChange={(e) => onMentorChange(parseInt(e.target.value))}
              >
                <option value="">강연자를 선택해주세요</option>
                {Array.isArray(mentors) && mentors.map((mentor) => (
                  <option key={mentor.id} value={mentor.id}>
                    {mentor.name}
                  </option>
                ))}
              </MentorSelect>
            </MentorSelection>

            <AnswerEditor>
              <RichTextEditor
                value={answerContent}
                onChange={onAnswerContentChange}
                placeholder="내용을 입력해주세요."
                minHeight="260px"
                attachmentsEditable={true}
              />
              <AnswerToolbar>
                <ToolbarLeft>
                  {/* RichTextEditor에서 제공하는 툴바 사용 */}
                </ToolbarLeft>
                <SubmitButton
                  onClick={onSubmit}
                  disabled={!answerContent.trim()}
                >
                  {isEditMode ? '수정하기' : '등록하기'}
                </SubmitButton>
              </AnswerToolbar>
            </AnswerEditor>

            {/* 첨부파일 목록 */}
            {attachments.length > 0 && (
              <AttachmentList>
                <AttachmentLabel>첨부파일</AttachmentLabel>
                {attachments.map((attachment) => (
                  <AttachmentItem key={attachment.id}>
                    <AttachmentName>{attachment.filename}</AttachmentName>
                    <AttachmentRemove
                      onClick={() => onAttachmentRemove(attachment.id)}
                    >
                      ×
                    </AttachmentRemove>
                  </AttachmentItem>
                ))}
              </AttachmentList>
            )}
          </AnswerSection>
        </AnswerModalContent>
      </AnswerModal>
    </ModalOverlay>
  );
};

// 삭제 확인 모달 컴포넌트
interface DeleteConfirmModalProps {
  onConfirm: () => void;
  onCancel: () => void;
}

const DeleteConfirmModal: React.FC<DeleteConfirmModalProps> = ({
  onConfirm,
  onCancel
}) => {
  return (
    <ModalOverlay onClick={onCancel}>
      <DeleteModal onClick={(e) => e.stopPropagation()}>
        <DeleteIcon>⚠️</DeleteIcon>
        <DeleteModalTitle>강의 질문을 삭제하시겠습니까?</DeleteModalTitle>
        <DeleteWarning>
          삭제된 강의 질문은 복구할 수 없습니다. 신중하게 결정해주세요.
        </DeleteWarning>

        <DeleteActions>
          <CancelButton
            onClick={onCancel}
          >
            취소
          </CancelButton>
          <ConfirmButton
            onClick={onConfirm}
          >
            삭제
          </ConfirmButton>
        </DeleteActions>
      </DeleteModal>
    </ModalOverlay>
  );
};

export default LectureQAManagementComponent;
